import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ActionsEntity, UserLegalEntity } from 'src/generated/graphql-types';
import {
  Investment,
  InvestorUser,
  Offering,
  Operator,
  OverviewTotals,
  profimexOverviewType,
  StatusCategoryFilterEnum,
} from 'src/types.d';
import { mergeEntityDetailsForInvestments } from 'src/utils/functions/investmentServices';
import { filterInvestmentByFilters } from 'src/utils/functions/investorServices';
import { RootState } from '../config/rootReducer';

//todo nir - set type like in the operator slice
const initialState = {
  investor: null,
  viewType: 'achieved',
  investments: null,
  overviewConvertedInvestments: null,
  overviewFilteredConvertedInvestments: null,
  activeOperator: null,
  entityView: {
    //holds the name and the id of the selected entity view
    name: 'All',
    id: null,
  },
  investorLegalEntities: [],
  shouldDisplayOperatorSelection: false,
  operatorFilterId: null,
  overviewTotals: {
    totalContributions: 0, //All contributions - active and past
    activeContributions: 0, // Only active contributions
    totalDistributions: 0, //All distributions
    totalPrincipalReturns: 0, //All principals return from overall distributions
    totalAccumulatedInterest: 0, //All accumulated interest from overall distributions
    totalCommitments: 0, //All commitments
    totalRemainingCommitments: 0, //All remaining commitments (commitments - contributions)
    totalRemainingCommitmentsConverted: 0, //All remaining commitments (commitments - contributions) not converted to USD
    overviewCurrencySymbol: '$', //Currency of the totals (either converted to USD or not)
    overviewCurrency: 'USD', //Currency of the totals (either converted to USD or not)
    convertToUSD: false,
  },
  overviewFilteredTotals: {
    totalContributions: 0, //All contributions - active and past
    activeContributions: 0, // Only active contributions
    totalDistributions: 0, //All distributions
    totalPrincipalReturns: 0, //All principals return from overall distributions
    totalAccumulatedInterest: 0, //All accumulated interest from overall distributions
    totalCommitments: 0, //All commitments
    totalRemainingCommitments: 0, //All remaining commitments (commitments - contributions)
    overviewCurrencySymbol: '$', //Currency of the totals (either converted to USD or not)
    overviewCurrency: 'USD', //Currency of the totals (either converted to USD or not)
    convertToUSD: false,
  },
  profimexOverviewTotals: [],
  investorSettings: 0,
  userBankAccountDetails: null,
  selectedProject: null,
  selectedFund: null,
  selectedOffering: null,
  navigationDict: null,
  selectedOperator: null,
  nestedMainNav: [],
  selectedDataPoint: null,
  investmentsByCategories: null,
  investmentsByArea: null,
  offerings: null,
  categoryFilter: StatusCategoryFilterEnum.ALL,
  managerDetails: null,
  transactions: null,
  investmentTotals: {},
  viewAsEmail: '',
  navMenuExpandedLock: false,
  offeringActions: [],
  offeringToasterShown: false, // flag to show the offering toast only once
  redirectUrl: null,
  relatedOperatorIds: [], //Holds the ids of the related operators linked to this investor
  openFilterMenu: false, //Whether to open the filter menu from another component
  selectedUpdate: null, //The selected update id or updates to all updates
};

const investor = createSlice({
  name: 'investor',
  initialState,
  reducers: {
    setInvestor(state, { payload }: PayloadAction<InvestorUser>) {
      state['investor'] = payload;
    },
    setInvestments(state, { payload }: PayloadAction<Investment[]>) {
      //sort the updates by date
      const investmentsWithSortedUpdates = payload?.map(investment => {
        //find the entity details and the updates
        const entityDetails =
          investment?.entityDetails ?? investment.projectDetails ?? investment.fundDetails;

        //find the updates
        const updates = entityDetails?.updatesDetails;

        //sort the updates by date
        const sortedUpdates = [...updates]?.sort(
          (a, b) => new Date(b.reportDate).getTime() - new Date(a.reportDate).getTime(),
        );
        return {
          ...investment,
          entityDetails: {
            ...entityDetails,
            updatesDetails: sortedUpdates,
          },
        };
      });

      //At this stage only the entityDetails are sorted - need to override the projectDetails and fundDetails with the entityDetails
      //override the projectDetails and fundDetails with the entityDetails
      const investmentsWithEntityDetails = investmentsWithSortedUpdates.map(investment => {
        if (investment.projectDetails) {
          return {
            ...investment,
            projectDetails: investment.entityDetails,
          };
        }
        if (investment.fundDetails) {
          return {
            ...investment,
            fundDetails: investment.entityDetails,
          };
        }

        return investment;
      });

      //update the sorted investments
      state['investments'] = investmentsWithEntityDetails;
    },
    setOverviewConvertedInvestments(state, { payload }: PayloadAction<Investment[]>) {
      state['overviewConvertedInvestments'] = payload;
    },
    setInvestmentsByCategories(state, { payload }: PayloadAction<any>) {
      state['investmentsByCategories'] = payload;
    },
    setInvestmentsByStates(state, { payload }: PayloadAction<any>) {
      state['investmentsByArea'] = payload;
    },
    setViewType(state, { payload }: PayloadAction<'achieved' | 'profimex'>) {
      state['viewType'] = payload;
    },
    setOverviewTotals(state, { payload }: PayloadAction<OverviewTotals>) {
      state['overviewTotals'] = payload;
    },
    setProfimexOverviewTotals(state, { payload }: PayloadAction<profimexOverviewType[]>) {
      state['profimexOverviewTotals'] = payload;
    },
    setInvestorActiveSettingsTab(state, { payload }: PayloadAction<number>) {
      state['investorSettings'] = payload;
    },
    setSelectedOperator(state, { payload }: PayloadAction<Operator>) {
      state['selectedOperator'] = payload;
    },
    setActiveOperator(state, { payload }: PayloadAction<Operator>) {
      state['activeOperator'] = payload;
    },
    setSelectedProject(state, { payload }: PayloadAction<Investment>) {
      state['selectedProject'] = payload;
    },
    setSelectedFund(state, { payload }: PayloadAction<Investment>) {
      state['selectedFund'] = payload;
    },
    setSelectedDataPoint(state, { payload }: PayloadAction<any>) {
      state['selectedDataPoint'] = payload;
    },
    setBankCountDetails(state, { payload }: PayloadAction<any>) {
      state['userBankAccountDetails'] = payload;
    },
    setNestedMainNav(state, { payload }: PayloadAction<any>) {
      state['nestedMainNav'] = payload;
    },
    setNavigationDict(state, { payload }: PayloadAction<any>) {
      state['navigationDict'] = payload;
    },
    setOfferings(state, { payload }: PayloadAction<Offering[]>) {
      state['offerings'] = payload as Offering[];
    },
    setEntityView(state, { payload }: PayloadAction<any>) {
      state['entityView'] = payload;
    },

    setOperatorFilterId(state, { payload }: PayloadAction<string>) {
      state['operatorFilterId'] = payload;
    },
    setShouldDisplayOperatorSelection(state, { payload }: PayloadAction<boolean>) {
      state['shouldDisplayOperatorSelection'] = payload;
    },
    setCategoryFilter(state, { payload }: PayloadAction<StatusCategoryFilterEnum>) {
      state['categoryFilter'] = payload;
    },
    setManagerDetails(state, { payload }: PayloadAction<any>) {
      state['managerDetails'] = payload;
    },
    setInvestorLegalEntities(state, { payload }: PayloadAction<UserLegalEntity[]>) {
      state['investorLegalEntities'] = payload;
    },
    setTransactions(state, { payload }: PayloadAction<any>) {
      state['transactions'] = payload;
    },
    setInvestmentTotals(state, { payload }: PayloadAction<any>) {
      state['investmentTotals'] = payload;
    },
    setViewAsEmail(state, { payload }: PayloadAction<string>) {
      state['viewAsEmail'] = payload;
    },

    setOverviewFilteredTotals(state, { payload }: PayloadAction<OverviewTotals>) {
      state['overviewFilteredTotals'] = payload;
    },
    setOverviewFilteredConvertedInvestments(state, { payload }: PayloadAction<Investment[]>) {
      state['overviewFilteredConvertedInvestments'] = payload;
    },
    setNavMenuExpandedLock(state, { payload }: PayloadAction<boolean>) {
      state['navMenuExpandedLock'] = payload;
    },
    setSelectedOffering(state, { payload }: PayloadAction<Offering>) {
      state['selectedOffering'] = payload;
    },
    setOfferingsActions(state, { payload }: PayloadAction<ActionsEntity[]>) {
      state['offeringActions'] = payload;
    },
    setOfferingToasterShown(state, { payload }: PayloadAction<boolean>) {
      state['offeringToasterShown'] = payload;
    },
    setRedirectUrl(state, { payload }: PayloadAction<string>) {
      //remove /dashboard from the url
      payload = payload.replace('/dashboard', '');
      state['redirectUrl'] = payload;
    },
    setRelatedOperatorIds(state, { payload }: PayloadAction<string[]>) {
      state['relatedOperatorIds'] = payload;
    },
    setOpenFilterMenu(state, { payload }: PayloadAction<boolean>) {
      state['openFilterMenu'] = payload;
    },

    setSelectedUpdate(state, { payload }: PayloadAction<string>) {
      state['selectedUpdate'] = payload;
    },
  },
});

const investmentsSelect = (state: RootState) => state.investor.investments;
const entityViewSelect = (state: RootState) => state.investor.entityView;
const categoryFilterSelect = (state: RootState) => state.investor.categoryFilter;

// get the selected fund and project
const selectedFundSelect = (state: RootState) => state.investor.selectedFund;
const selectedProjectSelect = (state: RootState) => state.investor.selectedProject;

/**
 * Select the operator from the state
 */
export const selectOperator = createSelector([investmentsSelect], investments => {
  const invWithOperator = investments?.find(investment => investment.operatorDetails);

  //return null if no operator found
  if (!invWithOperator) return null;
  return invWithOperator.operatorDetails;
});

/**
 * Creates filtered investment by the filtered entities and filtered categories
 */
export const selectFilteredInvestments = createSelector(
  [
    investmentsSelect,
    entityViewSelect,
    categoryFilterSelect,
    selectedFundSelect,
    selectedProjectSelect,
  ],
  (investments, entityView, categoryFilter, selectedFund, selectedProject) => {
    //get the selected entity id
    const selectedId: string = selectedFund
      ? selectedFund.fundDetails?.id
      : selectedProject
      ? selectedProject.projectDetails?.id
      : null;

    //get the selected legal entity id
    const selectedLegalEntityId: string = selectedFund
      ? selectedFund.legalEntityId
      : selectedProject
      ? selectedProject.legalEntityId
      : null;

    //filter the investments by the selected entity id
    let projectFundInvestment: Investment[] = selectedId
      ? investments?.filter(
          investment =>
            investment.projectDetails?.id === selectedId ||
            investment.fundDetails?.id === selectedId,
        ) ?? []
      : investments;

    //filter investment by the legal id (if there is a two different entities for that user which invested in the same project/fund)
    //apply only if there is a selected legal entity
    //this will apply only for a project/fund page and not for the overview page
    projectFundInvestment = projectFundInvestment.filter(
      investment => !selectedLegalEntityId || investment.legalEntityId === selectedLegalEntityId,
    );

    //merge the entity details for the investments
    projectFundInvestment = mergeEntityDetailsForInvestments(projectFundInvestment);

    //remove transfers only for the overview page
    const shouldFilterProfimexTransfers = !selectedLegalEntityId;

    //return the filtered investments
    const filtered = filterInvestmentByFilters(
      projectFundInvestment,
      entityView,
      categoryFilter,
      shouldFilterProfimexTransfers,
    );

    return filtered;
  },
);

//todo idan nir use this or the selected flitered investment instead of all fundDetails || projectDetails throughout the code
/**
 * Creates all filtered investments by the filtered entities and filtered categories
 */
export const selectAllFilteredInvestments = createSelector(
  [investmentsSelect, entityViewSelect, categoryFilterSelect],
  (investments, entityView, categoryFilter) => {
    //merge the entity details for the investments
    const allInvestments: Investment[] = mergeEntityDetailsForInvestments(investments);

    //return the filtered investments - apply the filter for the overview page
    return filterInvestmentByFilters(allInvestments, entityView, categoryFilter, true);
  },
);

/**
 * Creates investments projects list for the investor
 */
export const selectInvestorProjectsInvestments: (state: RootState) => Investment[] = createSelector(
  investmentsSelect,
  investmentList => {
    //filter all investments with projectDetails
    return investmentList?.filter(investment => investment.projectDetails) ?? [];
  },
);

/**
 * Creates investments funds list for the investor
 */
export const selectInvestorFundsInvestments: (state: RootState) => Investment[] = createSelector(
  investmentsSelect,
  investmentList => {
    //filter all investments with fundDetails
    return investmentList?.filter(investment => investment.fundDetails) ?? [];
  },
);

//todo idan should we add operator entity as well
/**
 * Creates investments funds and project list for the investor with a single entity details and an entity type
 * Sorted from funds to projects and then by name
 */
//todo idan nir should apply filter in here and then filtered investment will hold entity type instead of conditioning fundDetails or projectDetails
export const selectInvestorInvestmentsByEntityType: (state: RootState) => Investment[] =
  createSelector(investmentsSelect, investmentList => {
    return mergeEntityDetailsForInvestments(investmentList);
  });

// const entityViewSelect = (state: RootState) => state.investor.entityView;

// export const setInvestments: (state: RootState) => string[] = createSelector(
//   investmentsSelect,
//   entityViewSelect,
//   (investments, entityView) => {
//     if (!investments) return [];
//     if (entityView.name === 'All') return investments;

//     return investments.filter(investment => investment.legalEntityId === entityView.id);
//   },
// );

export const {
  setInvestor,
  setInvestments,
  setInvestmentsByCategories,
  setInvestmentsByStates,
  setViewType,
  setOverviewTotals,
  setInvestorActiveSettingsTab,
  setActiveOperator,
  setSelectedOperator,
  setSelectedProject,
  setSelectedFund,
  setSelectedOffering,
  setSelectedDataPoint,
  setBankCountDetails,
  setNestedMainNav,
  setNavigationDict,
  setOfferings,
  setProfimexOverviewTotals,
  setEntityView,
  setOverviewConvertedInvestments,
  setOperatorFilterId,
  setShouldDisplayOperatorSelection,
  setCategoryFilter,
  setManagerDetails,
  setInvestorLegalEntities,
  setTransactions,
  setInvestmentTotals,
  setViewAsEmail,
  setOverviewFilteredTotals,
  setOverviewFilteredConvertedInvestments,
  setNavMenuExpandedLock,
  setOfferingsActions,
  setOfferingToasterShown,
  setRedirectUrl,
  setRelatedOperatorIds,
  setOpenFilterMenu,
  setSelectedUpdate,
} = investor.actions;

export default investor.reducer;
